import { Sparkles, User, Volume2, VolumeX } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ChatMessage as ChatMessageType } from "@/lib/chatApi";
import { MathText } from "./MathText";
import { useSpeech } from "./useSpeech";

interface ChatMessageProps {
  message: ChatMessageType;
}

export function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === "user";
  const { speak, stop, isSpeaking, isSupported } = useSpeech();

  const handleSpeakToggle = () => {
    if (isSpeaking) {
      stop();
    } else {
      speak(message.content);
    }
  };

  return (
    <div className={cn("flex gap-3", isUser ? "flex-row-reverse" : "flex-row")}>
      {/* Avatar */}
      <div
        className={cn(
          "h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0",
          isUser
            ? "bg-zinc-200 dark:bg-zinc-700"
            : "bg-gradient-to-r from-violet-600 to-indigo-600",
        )}
      >
        {isUser ? (
          <User className="h-4 w-4 text-zinc-600 dark:text-zinc-300" />
        ) : (
          <Sparkles className="h-4 w-4 text-white" />
        )}
      </div>

      {/* Message bubble */}
      <div className="flex flex-col gap-1 max-w-[80%]">
        <div
          className={cn(
            "rounded-2xl px-4 py-2.5 text-sm",
            isUser
              ? "bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-tr-md"
              : "bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 rounded-tl-md",
          )}
        >
          {/* Render message content with math and formatting support */}
          <MathText content={message.content} />
        </div>

        {/* Read aloud button for assistant messages */}
        {!isUser && isSupported && (
          <button
            onClick={handleSpeakToggle}
            className={cn(
              "flex items-center gap-1 text-xs px-2 py-1 rounded-md w-fit transition-colors",
              isSpeaking
                ? "text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
            )}
            title={isSpeaking ? "Stop reading" : "Read aloud"}
          >
            {isSpeaking ? (
              <>
                <VolumeX className="h-3 w-3" />
                <span>Stop</span>
              </>
            ) : (
              <>
                <Volume2 className="h-3 w-3" />
                <span>Read aloud</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
